/* Discography Overrides Provider — single source of truth for admin overrides.
 *
 * - Fetches /api/discography/overrides ONCE at app mount
 * - Re-fetches automatically when:
 *     · `window.dispatchEvent(new Event("discography:invalidate"))` fires
 *     · The tab gains focus after >30s (visibilitychange)
 *     · A periodic refresh runs every 5 minutes
 * - Exposes the same shape as the legacy `useDiscographyOverrides()` hook
 *   so existing call-sites keep working without changes. */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ctx = createContext({
  overrides: {}, reload: () => {}, get: () => null,
  getCoverOverride: () => null, getTracklistOverride: () => null,
  getLinksOverride: () => null, getSeoOverride: () => null,
});

export function DiscographyOverridesProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const lastLoadRef = useRef(0);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/discography/overrides`);
      setOverrides(data || {});
      lastLoadRef.current = Date.now();
    } catch { /* stay with stale */ }
  }, []);

  // Initial + auto-refresh
  useEffect(() => {
    reload();
    const onInvalidate = () => reload();
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastLoadRef.current > 30_000) reload();
    };
    const interval = setInterval(reload, 5 * 60_000);
    window.addEventListener("discography:invalidate", onInvalidate);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener("discography:invalidate", onInvalidate);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);

  const get = useCallback((kind, id) => overrides[`${kind}:${id}`] || null, [overrides]);

  const value = {
    overrides, reload, get,
    getCoverOverride:     (kind, id) => get(kind, id)?.cover_url || null,
    getTracklistOverride: (kind, id) => get(kind, id)?.tracklist || null,
    getLinksOverride:     (kind, id) => {
      const links = get(kind, id)?.streaming_links;
      if (!links || !links.length) return null;
      return links.filter((l) => l.enabled !== false);
    },
    getSeoOverride: (kind, id) => {
      const o = get(kind, id);
      if (!o) return null;
      return { title: o.seo_title, description: o.seo_description, image: o.og_image };
    },
    /* Title / description text overrides for inline display on listings */
    getTitleOverride:       (kind, id) => get(kind, id)?.title_override || null,
    getDescriptionOverride: (kind, id) => get(kind, id)?.description_override || null,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Drop-in replacement for the old hook — same return shape. */
export default function useDiscographyOverrides() {
  return useContext(Ctx);
}

/** Public helper: any component (e.g. AdminDashboard after a save) can call
 *  this to force every subscriber to reload immediately. */
export function invalidateDiscographyOverrides() {
  window.dispatchEvent(new Event("discography:invalidate"));
}
